import type { LibraryFootage } from './library';

const JUNK = /^(\.DS_Store|\._.+|Thumbs\.db|desktop\.ini)$/i;

export interface FolderEntry {
  readonly kind: 'file' | 'directory';
  readonly name: string;
}

export interface FolderHandle {
  readonly name: string;
  getDirectoryHandle(name: string): Promise<FolderHandle>;
  removeEntry(name: string, options?: { recursive?: boolean }): Promise<void>;
  values(): AsyncIterable<FolderEntry>;
}

export interface DeletionPlan {
  readonly files: readonly (readonly string[])[];
  readonly folder: readonly string[] | undefined;
  readonly bytes: number;
}

export interface DeletionResult {
  readonly deleted: number;
  readonly bytes: number;
  readonly folderRemoved: boolean;
  readonly leftovers: readonly string[];
}

const isNamed = (error: unknown, name: string): boolean => error instanceof DOMException && error.name === name;

export function planDeletion(footage: LibraryFootage, rootName: string): DeletionPlan | undefined {
  const sidecars = [footage.eventJson, footage.aiJson, footage.thumb].flatMap((ref) => ref ?? []);
  const refs = [...footage.clips.map((clip) => clip.ref), ...sidecars];
  const relative = refs.map((ref) => ref.path.split('/'));
  if (relative.some((segments) => segments[0] !== rootName || segments.length < 2)) return undefined;
  const folder = footage.dir.split('/').slice(1);
  return {
    files: relative.map((segments) => segments.slice(1)),
    folder: footage.eventJson && folder.length > 0 ? folder : undefined,
    bytes: refs.reduce((sum, ref) => sum + ref.size, 0),
  };
}

async function resolve(root: FolderHandle, segments: readonly string[]): Promise<FolderHandle | undefined> {
  let folder = root;
  try {
    for (const segment of segments) folder = await folder.getDirectoryHandle(segment);
  } catch (error) {
    if (isNamed(error, 'NotFoundError')) return undefined;
    throw error;
  }
  return folder;
}

async function removeQuietly(folder: FolderHandle, name: string): Promise<boolean> {
  try {
    await folder.removeEntry(name);
    return true;
  } catch (error) {
    if (isNamed(error, 'NotFoundError')) return false;
    throw error;
  }
}

async function listNames(folder: FolderHandle): Promise<readonly FolderEntry[]> {
  const entries: FolderEntry[] = [];
  for await (const entry of folder.values()) entries.push({ kind: entry.kind, name: entry.name });
  return entries;
}

async function removeFolder(root: FolderHandle, segments: readonly string[]): Promise<readonly string[]> {
  const parent = await resolve(root, segments.slice(0, -1));
  const folder = await resolve(root, segments);
  const name = segments.at(-1)!;
  if (!parent || !folder) return [];
  const remaining = await listNames(folder);
  const leftovers = remaining.filter((entry) => entry.kind === 'directory' || !JUNK.test(entry.name)).map((entry) => entry.name);
  if (leftovers.length > 0) return leftovers;
  for (const entry of remaining) await removeQuietly(folder, entry.name);
  await removeQuietly(parent, name);
  return [];
}

export async function executeDeletion(root: FolderHandle, plan: DeletionPlan): Promise<DeletionResult> {
  let deleted = 0;
  for (const segments of plan.files) {
    const folder = await resolve(root, segments.slice(0, -1));
    const name = segments.at(-1)!;
    if (!folder) continue;
    if (await removeQuietly(folder, name)) deleted++;
    await removeQuietly(folder, `._${name}`);
  }
  const leftovers = plan.folder ? await removeFolder(root, plan.folder) : [];
  return { deleted, bytes: plan.bytes, folderRemoved: plan.folder !== undefined && leftovers.length === 0, leftovers };
}
